import { capitalize } from 'lib/util';
import AIJob, { AIJobOptions } from 'models/AIJob';
import Group, { GroupOptions } from 'models/Group';
import Review, {
  BaseReviewDataOptions,
  getDefaults as getReviewDefaults,
  ReviewOptions,
  ReviewType,
} from 'models/Review';

export type AdmissionDataPatient = {
  name?: string;
  dateOfBirth?: string | null;
  sex?: string;
};

export type AdmissionData = Partial<BaseReviewDataOptions> & {
  patient?: AdmissionDataPatient;
  referralSource?: string;
  planType?: string;
  denialReasonApiName?: string;
};

export interface AdmissionOptions extends Omit<ReviewOptions, 'data'> {
  type: ReviewType.ADMISSION;
  referredBy?: Partial<GroupOptions>;
  denialReasonOptions: { apiName: string; displayName: string }[];
  data: AdmissionData;
  patientDemographicsJob?: AIJobOptions | null;
  criteriaEvaluationJob?: AIJobOptions | null;
}

export function getDefaults(): AdmissionOptions {
  const defaults = getReviewDefaults();
  return {
    ...defaults,
    type: ReviewType.ADMISSION,
    referredBy: {} as GroupOptions,
    denialReasonOptions: [],
    data: {} as AdmissionData,
    patientDemographicsJob: null,
    criteriaEvaluationJob: null,
  };
}

export default class Admission extends Review {
  declare type: ReviewType.ADMISSION;
  referredBy?: Group;
  denialReasonOptions: { apiName: string; displayName: string }[];
  data: AdmissionData;
  patientDemographicsJob: AIJob | null;
  criteriaEvaluationJob: AIJob | null;

  constructor(options: Partial<AdmissionOptions> = {}) {
    const opts = { ...getDefaults(), ...options };

    super(opts);

    this.referredBy = options.referredBy ? new Group(options.referredBy) : options.referredBy;
    this.denialReasonOptions = options.denialReasonOptions || [];
    this.data = options.data || ({} as AdmissionData);
    this.patientDemographicsJob = opts.patientDemographicsJob ? new AIJob(opts.patientDemographicsJob) : null;
    this.criteriaEvaluationJob = opts.criteriaEvaluationJob ? new AIJob(opts.criteriaEvaluationJob) : null;
  }

  static get type() {
    return ReviewType.ADMISSION;
  }

  get isProcessing() {
    return !!(
      this.summaryJobForCurrentAttachments?.isProcessing ||
      this.criteriaEvaluationJob?.isProcessing ||
      this.patientDemographicsJob?.isProcessing
    );
  }

  get statusLabel() {
    return this.rejected ? 'Denied' : capitalize(this.status);
  }
}
